import React, { useEffect, useState } from "react";
import {
  CardContent,
  CardActions,
  CardHeader,
  InputLabel,
  Grid,
  Box,
  Button,
  Link
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField } from "formik-material-ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReCaptchaV2 from "react-google-recaptcha";
import { useDispatch } from "react-redux";

import { SessionSorageInstance } from "../../Services/SessionStorageService";
import { setUser, setuserRol } from "../../Redux/GeneralSlice";
import { setDefaultAccount } from "../../Redux/AccountSlice";
import IndexService from "../../Services/http/IndexService";

import userRoles from "../../configs/userRolesConfigs";
import LockIcon from '@mui/icons-material/Lock';

import VerificationCodeForm from "./VerificationCodeForm";
import HubspotUpdateSuccessDialog from "../Dialogs/HubspotUpdateSuccessDialog";

import "./loginForm.scss";
import { useTranslation } from 'react-i18next';
import OnboardingLayout from "../Layout/OnboardingLayout";

const initialValues = {
  email: "",
  password: "",
  recaptcha: "",
};

let validationSchema = Yup.object().shape({
  email: Yup.string().required("Required"),
  password: Yup.string().required("Required!"),
});

const LoginForm = () => {
  const { t, i18n } = useTranslation("loginForm");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const navigate = useNavigate();
  const [isShowErrorMessage, setIsShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userToken, setUserToken] = useState("");
  const [responseLogin, setResponseLogin] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSuccessFirstStep, setIsSuccessFirstStep] = useState(false);
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState(false);
  const redirect_to = searchParams.get("redirect_to");

  const dispatch = useDispatch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("hubspot_val")) {
      handleHubspotVal(params.get("hubspot_val"));
      removeParam("hubspot_val");
    }
  }, []);
  function removeParam(key) {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete(key);
    setSearchParams(newParams.toString());
  }

  const handleHubspotVal = (hubspot_val) => {
    // update lead status of the contact
    IndexService.updateContactStatusInHubSpot(hubspot_val).then(
      response => {
        if (response) {
          if (response["message"] === "success") {
            setIsOpenSuccessDialog(true);
          } else if (response["message"] === "invalid value" || response["message"] === "not found user") {
            alert(t("errors.userNotFound"));
          } else if (response["message"] === "error update contact") {
            alert(t("errors.updateError"));
          } else {
            alert(t("errors.generic"));
          }
        }
      }
    );
  };



  const getErrorMessage = (responseMessage: string) => {
    switch (responseMessage) {
      case null: {
        return t("errors.connection");
      }
      case "success": {
        return "success";
      }
      case "Password expired": {
        return t("errors.passwordExpired");
      }
      case "Unauthenticated: invalid email or password": {
        return t("errors.invalidCredentials");
      }
    }
  };


  const onSubmitLogin = async (values: any) => {
    setIsShowErrorMessage(false);
    const response = await IndexService.login(values);
    if (response && response["token"]) {
      setResponseLogin(response);
      const userRole = response["user"]["role"];
      if (userRole === userRoles.admin || userRole === userRoles.analyst) {
        setUserToken(response["token"]);
        setIsSuccessFirstStep(true);
      } else {
        onLoginSuccess();
      }

    } else {
      let message: string =
        getErrorMessage(
          JSON.parse(JSON.stringify(response))
            ? JSON.parse(JSON.stringify(response)).message
            : null
        ) || "";
      setErrorMessage(message);
      setIsShowErrorMessage(true);
    }
  };

  const onLoginSuccess = () => {
    SessionSorageInstance.setUserDetails(responseLogin);
    dispatch(setuserRol(SessionSorageInstance.getUserRole()));
    dispatch(setUser(SessionSorageInstance.getUserDetails()));
    if (responseLogin['account']) {
      dispatch(setDefaultAccount(responseLogin['account']));
      navigate('/appApproval')
    }
    else if (redirect_to) {
      navigate(redirect_to);
    }
    else {
      if (responseLogin['user']['role'] === 'analyst') {
        navigate('/layout/administrator/viewStatus');
      } else {
        navigate("/layout");
      }
    }
  };

  const onChange = (value: any) => {
    value ? setDisableSubmit(false) : setDisableSubmit(true);
  };


  const returnSiteKey = () => {
    let siteKey: string = process.env.REACT_APP_RECAPTCHA_SITE_KEY as string;
    return siteKey;
  };

  return (
    <OnboardingLayout rightClassName="center-items">
        <Grid className="login-grid" container justifyContent="center" sx={{ margin: 'auto' }}>
          <Grid item md={6}>
            <CardHeader title={t("title")} className="text-center pb-0" />
            {!isSuccessFirstStep && <p className="text-center">{t("subtitle")}</p>}
            {!isSuccessFirstStep && <Formik
              initialValues={initialValues}
              onSubmit={onSubmitLogin}
              validationSchema={validationSchema}
            >
              {({ dirty, isValid, values }) => {
                return (
                  <Form>
                    <CardContent>
                      <Grid item container spacing={1} justifyContent="center">
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("email")}</InputLabel>
                          <Field
                            label=""
                            fullWidth
                            required
                            placeholder={t("placeholders.email")}
                            name="email"
                            value={values.email}
                            component={TextField}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12}>
                          <InputLabel>{t("password")}</InputLabel>
                          <Field
                            label=""
                            variant="outlined"
                            fullWidth
                            placeholder={t("placeholders.password")}
                            name="password"
                            value={values.password}
                            type="password"
                            required
                            component={TextField}
                          />
                        </Grid>
                        <Grid item>
                          <ReCaptchaV2
                            key={i18n.language}
                            sitekey={returnSiteKey()}
                            onChange={onChange}
                            hl={i18n.language}
                            style={{ marginTop: "8px" }}
                          />
                        </Grid>
                        {isShowErrorMessage && (
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <div className="error-message">{errorMessage}</div>
                          </div>
                        )}
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button
                        disabled={!dirty || !isValid}
                        type="submit"
                        className='button-login'
                        startIcon={<LockIcon className="white-color" />}
                      >
                        {t("submit")}
                      </Button>

                    </CardActions>
                    <p className="text-center gray-two-color">
                      {t("no_account")}
                      <Link className="blue-color pl-3p" underline="hover" href="/registerForm">
                        {t("signup")}
                      </Link>
                    </p>
                  </Form>
                );
              }}
            </Formik>}
            {isSuccessFirstStep && <VerificationCodeForm
              token={userToken} onLoginSuccess={onLoginSuccess}></VerificationCodeForm>}
          </Grid>
        </Grid>

      {isOpenSuccessDialog && <HubspotUpdateSuccessDialog></HubspotUpdateSuccessDialog>}
    </OnboardingLayout>
  );

};

export default LoginForm;
